/**
 * DE DÓNDE SALE EL PRECIO DE UN RENGLÓN — las reglas, en un solo lugar.
 *
 * Por qué existe. Un renglón puede tener DOS precios a la vez: el que se encontró en los
 * catálogos del cliente y el que armó la inteligencia artificial. Solo uno cuenta en el
 * total, y hasta hoy el de la inteligencia artificial reemplazaba al del catálogo sin que
 * nadie lo notara ni pudiera volver. Caso real de un tester (agosto 2026): un concepto de
 * enfierradura tenía el precio de su catálogo con coincidencia perfecta y quedó cotizado
 * 4,3 veces más caro; sus dos hermanos, con el precio del catálogo, quedaron bien.
 *
 * Por qué en `shared`: la pantalla tiene que avisar ANTES (cuántos renglones van a perder
 * el precio de su catálogo si se generan con inteligencia artificial) con la misma regla
 * con la que el servidor decide después a cuál se puede volver. Con la regla en dos lados
 * se despegan en el primer cambio y el aviso promete algo distinto de lo que pasa.
 *
 * NO hay umbral nuevo acá: "firme" es la confianza `alta` que ya asigna el buscador de
 * equivalentes (umbral `match_verde_min` de la calibración) o el veredicto SÍ de la
 * verificación. Esto solo la lee.
 */

/** Lo mínimo que hay que saber de un renglón para decidir de dónde sale su precio. */
export interface RenglonConPrecio {
  /** Confianza VIGENTE del renglón. `sintetica` = hoy cuenta el precio de la IA. */
  confianza: string;
  /** Precio unitario que hoy cuenta en el total. */
  precioUnitario: number | null;
  /**
   * Precio del catálogo guardado antes de que la IA lo reemplazara. `null`/0 = no había
   * (el renglón nunca tuvo equivalente), y entonces no hay a dónde volver.
   */
  precioCatalogoGuardado?: number | null;
  /** Confianza que tenía ese match guardado (`alta` / `media` / …). */
  confianzaCatalogoGuardada?: string | null;
  /** Precio que armó la inteligencia artificial, esté activo o no. */
  precioIa?: number | null;
  /** Los agrupadores (partidas) no tienen precio propio. */
  kind?: string;
}

/** ¿Cuál de los dos precios cuenta HOY en el total de la hoja? */
export function fuenteActivaDelPrecio(r: RenglonConPrecio): 'ia' | 'catalogo' {
  return r.confianza === 'sintetica' ? 'ia' : 'catalogo';
}

const conPrecio = (n: number | null | undefined): boolean => Number(n) > 0;

/**
 * ¿HOY este renglón vale con un precio que salió de los catálogos del cliente?
 * (Un renglón sin precio, o con el de la inteligencia artificial, no.)
 */
export function precioVieneDelCatalogo(r: RenglonConPrecio): boolean {
  if (r.kind === 'group') return false;
  return fuenteActivaDelPrecio(r) === 'catalogo' && conPrecio(r.precioUnitario);
}

/**
 * ¿Es un equivalente que el sistema dio por bueno? Es la confianza `alta`: la que el
 * buscador de equivalentes reserva para el que pasa el umbral de la calibración o el que
 * la verificación confirmó.
 *
 * Sirve para AVISAR, no para decidir: los encendidos "en bloque" sí los incluyen ("todos"
 * quiere decir todos), y pedir el análisis con inteligencia artificial de uno de estos es
 * legítimo — hay catálogos de cliente con la mano de obra muy por debajo de la realidad, y
 * ahí el precio armado por la inteligencia artificial es el más cercano. Lo que se hace con
 * esta cuenta es decirle al cotizador cuántos son, antes de que gaste.
 */
export function equivalenteFirme(r: RenglonConPrecio): boolean {
  return precioVieneDelCatalogo(r) && r.confianza === 'alta';
}

/**
 * ¿La fila que eligió el buscador alcanza para poner un precio?
 *
 * Un catálogo de conceptos puede venir pelado: los renglones están, pero sin su análisis
 * detrás. Esa fila es un dato legítimo (viaja de vuelta a OPUS 27, donde la receta la pone
 * él), pero **no puede pasar por equivalente**: no hay número que mostrar. Antes se guardaba
 * igual y el renglón quedaba **verde, con confianza alta y en cero** — la peor forma de
 * equivocarse, porque suma cero al total sin una sola marca. Y de paso el match le cerraba
 * la única puerta que le daba precio: la cotización con inteligencia artificial sólo toma
 * los renglones que dicen "sin equivalente".
 *
 * Se mide por el precio, no por si trae desglose: una fila con desglose cuyos insumos están
 * todos en cero tampoco tiene precio. Es el mismo criterio que ya se aplica al análisis
 * armado por la inteligencia artificial (cero = no se pudo costear).
 */
export function equivalenteAlcanzaParaPrecio(res: { precioUnitario: number | null }): boolean {
  return conPrecio(res.precioUnitario);
}

/** ¿Hay un precio de catálogo guardado al que se pueda volver en un clic? */
export function puedeVolverAlCatalogo(r: RenglonConPrecio): boolean {
  return fuenteActivaDelPrecio(r) === 'ia' && conPrecio(r.precioCatalogoGuardado);
}

/** ¿Hay un precio armado por la inteligencia artificial al que se pueda volver en un clic? */
export function puedeVolverALaIa(r: RenglonConPrecio): boolean {
  return fuenteActivaDelPrecio(r) === 'catalogo' && conPrecio(r.precioIa);
}

/** ¿Este renglón tiene los DOS precios y hay algo que elegir? */
export function tieneLosDosPrecios(r: RenglonConPrecio): boolean {
  return puedeVolverAlCatalogo(r) || puedeVolverALaIa(r);
}

/**
 * Cuántas veces el precio de la inteligencia artificial entra en el del catálogo.
 * Es un DATO que se muestra tal cual ("es 4,3 veces el de tu catálogo"), no un umbral:
 * nada se decide con este número. `null` cuando falta alguno de los dos.
 */
export function vecesElPrecioDelCatalogo(
  precioIa: number | null | undefined,
  precioCatalogo: number | null | undefined,
): number | null {
  if (!conPrecio(precioIa) || !conPrecio(precioCatalogo)) return null;
  return Number(precioIa) / Number(precioCatalogo);
}

export interface PrecioEnRiesgo {
  /** Renglones con equivalente FIRME (confianza alta) que perderían su precio. */
  firmes: number;
  /** Renglones con equivalente dudoso (confianza media) que perderían su precio. */
  dudosos: number;
  /** Los dos juntos: cuántos precios de catálogo se van a reemplazar. */
  total: number;
}

/**
 * De los renglones que se van a generar con inteligencia artificial, cuántos ya tienen
 * precio salido de los catálogos del cliente. Es lo que se le dice ANTES de gastar: el
 * precio nuevo va a reemplazar al del catálogo en el total.
 */
export function preciosDeCatalogoEnRiesgo(renglones: RenglonConPrecio[]): PrecioEnRiesgo {
  let firmes = 0;
  let dudosos = 0;
  for (const r of renglones) {
    if (!precioVieneDelCatalogo(r)) continue;
    if (r.confianza === 'alta') firmes++;
    else dudosos++;
  }
  return { firmes, dudosos, total: firmes + dudosos };
}
